import type { FC } from 'react';
import { useEffect, useRef } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Outlet, useLocation, useOutlet } from 'react-router-dom';

import type { SidebarAccount } from '@polyai/ui/components/molecules/AccountsSidebar';

import AccountSidePanel from 'components/molecules/AccountSidePanel';
import { IAAccountSidebar } from 'components/organisms/IAAccountSidebar';
import { useAppSelector } from 'hooks/reduxHooks';
import useAccounts from 'hooks/useAccounts';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch';
import useProjectRoute from 'hooks/useProjectRoute';
import { logError } from 'lib/ddlogHelper';
import * as Styled from './LandingLayout.styled';

const isPersonalAccount = (name: string, userEmail?: string) =>
  userEmail && name.includes(userEmail);

const getSidebarAccountName = (name: string, userEmail?: string) => {
  if (isPersonalAccount(name, userEmail)) {
    return 'My Workspace';
  }

  return name;
};

const MAX_ABBREVIATION_LENGTH = 3;

export const getSidebarAccountAbbreviation = (
  name: string,
  userEmail?: string,
): string => {
  if (isPersonalAccount(name, userEmail)) {
    return 'MW';
  }
  if (name === 'PLATFORM') {
    return 'PLT';
  }
  if (name === 'PolyAIDemos') {
    return 'DMO';
  }

  const finalName = name.includes('@poly') ? name.split('@')[0] : name;

  const getInitials = (pattern: RegExp) =>
    (finalName.match(pattern) ?? [])
      .slice(0, MAX_ABBREVIATION_LENGTH)
      .join('')
      .toUpperCase();

  const wordInitials = getInitials(/\b[A-Za-z]/g);
  if (wordInitials.length >= 2) {
    return wordInitials;
  }

  const capitalLetters = getInitials(/\b[A-Za-z]|\B[A-Z]/g);
  if (capitalLetters.length >= 2) {
    return capitalLetters;
  }

  const consonants = getInitials(/^[a-zA-Z]|[^aeiouAEIOU]/g);

  if (consonants.length >= 2) {
    return consonants;
  }

  return finalName.slice(0, MAX_ABBREVIATION_LENGTH).toUpperCase();
};

const LandingLayout: FC = () => {
  const { accountId } = useProjectRoute();
  const { isFeatureOn } = useIsFeatureOn();
  const isIAEnabled = isFeatureOn('ia_q2_26');

  const { accounts, isLoadingAccounts } = useAccounts();

  const user = useAppSelector(state => state.auth.user);

  const sidebarAccounts: SidebarAccount[] = accounts.map(account => ({
    id: account.id,
    name: getSidebarAccountName(account.name, user?.email),
    abbreviation: getSidebarAccountAbbreviation(account.name, user?.email),
  }));

  useEffect(() => {
    if (accountId) {
      datadogRum.setGlobalContextProperty('account_id', accountId);
      datadogLogs.setGlobalContextProperty('account_id', accountId);
    }

    return () => {
      datadogRum.removeGlobalContextProperty('account_id');
      datadogLogs.removeGlobalContextProperty('account_id');
    };
  }, [accountId]);

  const location = useLocation();
  const outlet = useOutlet();
  const loggedPathRef = useRef<string | null>(null);

  const hasChildMatch = outlet !== null;

  useEffect(() => {
    if (!hasChildMatch && loggedPathRef.current !== location.pathname) {
      logError(
        new Error('LandingLayout: no child route matched — Outlet is null'),
        {
          source: 'error_boundary',
          componentName: 'LandingLayout',
          extra: {
            pathname: location.pathname,
          },
        },
        accountId,
      );
      loggedPathRef.current = location.pathname;
    }
    if (hasChildMatch) {
      loggedPathRef.current = null;
    }
  }, [hasChildMatch, location.pathname, accountId]);

  return (
    <Styled.PageWrapper>
      {isIAEnabled ? (
        <IAAccountSidebar />
      ) : (
        <AccountSidePanel
          accounts={sidebarAccounts}
          activeAccountId={accountId}
          isLoadingAccounts={isLoadingAccounts}
        />
      )}
      <Outlet />
    </Styled.PageWrapper>
  );
};

export default LandingLayout;
